import React from 'react';
import { Box, Flex, Skeleton, SimpleGrid, Stack } from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';
import { FramedCard, FramedCardContent } from '../ui';

export type DataSkeletonVariant = 'table' | 'cards' | 'form' | 'detail';

export interface DataSkeletonProps {
  variant?: DataSkeletonVariant;
  rows?: number;
  columns?: number;
  label?: string;
}

export function DataSkeleton({
  variant = 'table',
  rows = 6,
  columns = 4,
  label = 'Carregando dados',
}: DataSkeletonProps) {
  const reducedMotion = useReducedMotion();
  const motion = reducedMotion ? { animation: 'none' } : { speed: 0.8 };
  const status = {
    role: 'status',
    'aria-label': label,
    'aria-busy': true,
    'data-testid': 'data-skeleton',
  } as const;

  if (variant === 'cards') {
    return (
      <FramedCard {...status}>
        <FramedCardContent>
          <Flex direction={{ base: 'column', sm: 'row' }}>
            {Array.from({ length: rows }).map((_, index) => (
              <Box
                key={`kpi-${index + 1}`}
                flex="1"
                px="16px"
                py="12px"
                borderLeftWidth={{ base: '0', sm: index === 0 ? '0' : '1px' }}
                borderLeftStyle="solid"
                borderLeftColor="line.hairline"
              >
                <Skeleton h="12px" w="60%" borderRadius="6px" {...motion} />
                <Skeleton h="20px" w="40%" mt="10px" borderRadius="6px" {...motion} />
              </Box>
            ))}
          </Flex>
        </FramedCardContent>
      </FramedCard>
    );
  }

  if (variant === 'form') {
    return (
      <Stack {...status} spacing="24px">
        <Skeleton h="20px" w="180px" borderRadius="6px" {...motion} />
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
          {Array.from({ length: rows }).map((_, index) => (
            <Box key={`field-${index + 1}`}>
              <Skeleton h="12px" w="88px" mb="8px" borderRadius="6px" {...motion} />
              <Skeleton h="38px" borderRadius="10px" {...motion} />
            </Box>
          ))}
        </SimpleGrid>
      </Stack>
    );
  }

  if (variant === 'detail') {
    return (
      <SimpleGrid {...status} columns={{ base: 2, md: 3 }} spacing="8px">
        {Array.from({ length: rows }).map((_, index) => (
          <Box
            key={`metric-${index + 1}`}
            p="12px"
            border="1px solid"
            borderColor="line.hairline"
            borderRadius="lg"
          >
            <Skeleton h="12px" w="70%" borderRadius="6px" {...motion} />
            <Skeleton h="18px" w="40%" mt="8px" borderRadius="6px" {...motion} />
          </Box>
        ))}
      </SimpleGrid>
    );
  }

  return (
    <FramedCard {...status}>
      <FramedCardContent>
        <Flex
          h="40px"
          align="center"
          gap="24px"
          px="16px"
          bg="surface.frame"
          borderBottom="1px solid"
          borderColor="line.hairline"
        >
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton
              key={`head-${index + 1}`}
              h="10px"
              w={index === 0 ? '120px' : '72px'}
              borderRadius="6px"
              {...motion}
            />
          ))}
        </Flex>

        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Flex
            key={`row-${rowIndex + 1}`}
            h="44px"
            align="center"
            gap="24px"
            px="16px"
            borderBottom="1px solid"
            borderColor="line.hairline"
            _last={{ borderBottom: 'none' }}
          >
            <Flex align="center" gap="10px" w="220px">
              <Skeleton w="20px" h="20px" borderRadius="6px" flexShrink={0} {...motion} />
              <Box flex="1">
                <Skeleton h="10px" w="70%" borderRadius="6px" {...motion} />
                <Skeleton h="8px" w="45%" mt="6px" borderRadius="6px" {...motion} />
              </Box>
            </Flex>
            {Array.from({ length: Math.max(columns - 1, 1) }).map((__, cellIndex) => (
              <Skeleton
                key={`cell-${rowIndex + 1}-${cellIndex + 1}`}
                h="10px"
                w="88px"
                borderRadius="6px"
                {...motion}
              />
            ))}
          </Flex>
        ))}

        <Flex
          h="56px"
          align="center"
          justify="space-between"
          px="12px"
          borderTop="1px solid"
          borderColor="line.hairline"
        >
          <Skeleton h="10px" w="88px" borderRadius="6px" {...motion} />
          <Flex gap="4px" align="center">
            <Skeleton h="32px" w="88px" borderRadius="10px" {...motion} />
            <Skeleton h="32px" w="32px" borderRadius="10px" {...motion} />
            <Skeleton h="32px" w="88px" borderRadius="10px" {...motion} />
          </Flex>
        </Flex>
      </FramedCardContent>
    </FramedCard>
  );
}
